/**
 * Champs d'un chemin de l'organisation : nom, accès, affichage et synchro OSM (si public),
 * type (avec son équivalent OSM), utilisations groupées par thème, vitesse, notes.
 * Idées reprises de l'éditeur de fin août (branche backup/osmmap-wip-20260830), en plus simple :
 * une utilisation est cochée ou non, sans les valeurs d'accès OSM (yes, permissive, permit…).
 */
import { useTranslation } from 'react-i18next'
import type { Choice, PathAccess, PathDisplay } from '../../api'

export type PathForm = {
  path_type: string
  access: PathAccess
  display: PathDisplay
  osm_sync_requested: boolean
  usages: string[]
  name: string
  notes: string
  speed_limit_kmh: string
}

/** Types où une limite de vitesse a du sens. */
const SPEED_TYPES = new Set(['road', 'track', 'access', 'vehicle', 'slow', 'service'])

const USAGE_GROUPS: { key: string; usages: string[] }[] = [
  { key: 'pied', usages: ['walking', 'hiking', 'snowshoe'] },
  { key: 'ski', usages: ['nordic_classic', 'nordic_skate', 'nordic_backcountry'] },
  { key: 'velo', usages: ['cycling', 'mtb', 'fatbike'] },
  { key: 'equestre', usages: ['horse'] },
  { key: 'motorise', usages: ['car', 'atv', 'snowmobile'] },
]

type Props = {
  form: PathForm
  onChange: (update: (f: PathForm) => PathForm) => void
  pathTypes: Choice[]
  pathUsages: Choice[]
}

export function PathFields({ form, onChange, pathTypes, pathUsages }: Props) {
  const { t } = useTranslation('gestion')
  const set = (patch: Partial<PathForm>) => onChange((f) => ({ ...f, ...patch }))
  const usageLabel = new Map(pathUsages.map((u) => [u.value, u.label]))
  const known = new Set(USAGE_GROUPS.flatMap((g) => g.usages))
  const others = pathUsages.filter((u) => !known.has(u.value)).map((u) => u.value)
  const groups = others.length ? [...USAGE_GROUPS, { key: 'autres', usages: others }] : USAGE_GROUPS
  const osm = pathTypes.find((p) => p.value === form.path_type)?.osm

  return (
    <>
      <label className="g-field">
        <span className="g-field__label">{t('carte.nom')}</span>
        <input className="g-input" value={form.name} placeholder={t('carte.nom_exemple')} onChange={(e) => set({ name: e.target.value })} />
      </label>

      <div className="g-field">
        <span className="g-field__label">{t('carte.acces')}</span>
        <div className="g-segmented g-segmented--full" role="radiogroup" aria-label={t('carte.acces')}>
          {(['public', 'private'] as const).map((a) => (
            <button
              key={a}
              type="button"
              role="radio"
              aria-checked={form.access === a}
              className={`g-segmented__item${form.access === a ? ' g-segmented__item--active' : ''}`}
              onClick={() => set({ access: a })}
            >
              <span className={`mdi ${a === 'private' ? 'mdi-lock-outline' : 'mdi-earth'}`} aria-hidden /> {t(`carte.acces_${a}`)}
            </button>
          ))}
        </div>
        <span className="g-hint">{form.access === 'private' ? t('carte.aide_prive') : t('carte.aide_public')}</span>
      </div>

      {form.access === 'public' && (
        <div className="g-field">
          <span className="g-field__label">{t('carte.affichage')}</span>
          <div className="g-options" role="radiogroup" aria-label={t('carte.affichage')}>
            {(['network', 'organization'] as const).map((d) => (
              <label key={d} className={`g-option${form.display === d ? ' g-option--active' : ''}`}>
                <input type="radio" name="path-display" checked={form.display === d} onChange={() => set({ display: d })} />
                <span>
                  <strong>{t(`carte.affichage_${d}`)}</strong>
                  <span className="g-hint">{t(`carte.aide_affichage_${d}`)}</span>
                </span>
              </label>
            ))}
          </div>
          {form.display === 'network' && (
            <label className="g-check">
              <input type="checkbox" checked={form.osm_sync_requested} onChange={(e) => set({ osm_sync_requested: e.target.checked })} />
              <span>
                <strong>{t('carte.synchro_osm')}</strong>
                <span className="g-hint">{t('carte.aide_synchro_osm')}</span>
              </span>
            </label>
          )}
        </div>
      )}

      <label className="g-field">
        <span className="g-field__label">{t('carte.type')}</span>
        <select className="g-input" value={form.path_type} onChange={(e) => set({ path_type: e.target.value })}>
          {pathTypes.map((pt) => (
            <option key={pt.value} value={pt.value}>
              {pt.label}
            </option>
          ))}
        </select>
        {osm && <span className="g-hint">{t('carte.equivalent_osm', { tag: `highway=${osm}` })}</span>}
      </label>

      <div className="g-field">
        <span className="g-field__label">{t('carte.utilisation')}</span>
        {groups.map((g) => (
          <div key={g.key} className="g-usage-group">
            <span className="g-usage-group__title">{t(`carte.theme_${g.key}`)}</span>
            <div className="g-chips" role="group" aria-label={t(`carte.theme_${g.key}`)}>
              {g.usages
                .filter((u) => usageLabel.has(u))
                .map((u) => {
                  const on = form.usages.includes(u)
                  return (
                    <button
                      key={u}
                      type="button"
                      aria-pressed={on}
                      className={`g-chip g-chip--sm${on ? ' g-chip--active' : ''}`}
                      onClick={() =>
                        onChange((f) => ({ ...f, usages: f.usages.includes(u) ? f.usages.filter((x) => x !== u) : [...f.usages, u] }))
                      }
                    >
                      {on && <span className="mdi mdi-check" aria-hidden />}
                      {usageLabel.get(u)}
                    </button>
                  )
                })}
            </div>
          </div>
        ))}
      </div>

      {SPEED_TYPES.has(form.path_type) && (
        <label className="g-field">
          <span className="g-field__label">{t('carte.vitesse')}</span>
          <input
            className="g-input"
            type="number"
            min={1}
            max={120}
            value={form.speed_limit_kmh}
            onChange={(e) => set({ speed_limit_kmh: e.target.value })}
          />
        </label>
      )}

      <label className="g-field">
        <span className="g-field__label">{t('carte.notes')}</span>
        <textarea className="g-input" rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
      </label>
    </>
  )
}
